import { readEnvelope, ApiError } from "./envelope";

// auth.ts — the real, shared session-cookie + CSRF client every v2 app uses (v2-plan S7.0,
// G47 corrected): mailbox and panel sessions are the SAME /auth/login + cookie + CSRF
// mechanism (verified directly against the Go server, not assumed — see docs/history/V2-UI-PLAN.md's
// S7.0/G47 correction). This is not a mailbox-specific shim to be replaced later; it is
// V6.2's actual session layer, built now because mailbox needs it first.
//
// What this deliberately does NOT do yet (V6.2's remaining work, not this file's job):
// role-guarded routing, typegen against the OpenAPI spec, token-based (non-cookie) auth.

export interface WhoAmI {
  /** Whether this identity has a confirmed second factor. ABSENT when the server could not
   *  determine it — which is not the same as false, and rendering it as false would tell someone
   *  to fix protection that may already be in place. */
  two_factor?: boolean;
  email: string;
  role: string;
  via_token: boolean;
  tenant_id: number | null;
  domain_id: number | null;
  scopes: string[] | null;
  csrf_token: string;
  impersonated_by?: string;
  impersonating?: string;
  note?: string;
  /** The zone this login's consoles show times in: its override, else its account's, else UTC. */
  time_zone?: string;
  /** The account's own zone (absent for an operator). */
  account_time_zone?: string;
  /** This login's override; "" follows the account. */
  display_time_zone?: string;
  /** Why a zone could not be read (times are then shown in UTC). */
  time_zone_error?: string;
}

export interface LoginResult {
  status: "active" | "2fa_required";
  csrf_token?: string;
  role?: string;
  email?: string;
}

const MUTATING = new Set(["POST", "PUT", "PATCH", "DELETE"]);

/**
 * API_PREFIX — EVERY normal route lives under /api/v1 (internal/server/server.go:
 * `docs: apidoc.NewRegistry("/api/v1")`, which prefixes every module/auth route it
 * documents). v1's own mailbox.html hardcodes the identical prefix (`const API="/api/v1"`)
 * on every call including /auth/login itself.
 *
 * Found the hard way (2026-08-27): apiFetch originally called bare paths like "/auth/login"
 * with no prefix — which only ever "worked" in the sense that nothing caught it, because no
 * call had been run against a real server until this was checked directly against one built
 * from this exact repo. Every call in this client and api.ts was silently 404ing.
 */
const API_PREFIX = "/api/v1";

export class AuthClient {
  private csrfToken = "";

  /** The CSRF token from the most recent login/whoami — apiFetch attaches it automatically. */
  get csrf(): string {
    return this.csrfToken;
  }

  /**
   * apiFetch is the ONE fetch wrapper every v2 app should use for a normal (/api/v1-mounted)
   * route: same-origin credentials (the session cookie), the /api/v1 prefix, X-CSRF-Token on
   * every mutating request (matching internal/server/server.go's own check against
   * p.SessionCSRF), and envelope decoding (throws ApiError on ok:false or a non-2xx status,
   * so a caller's .catch/error boundary handles every failure the same way rather than each
   * call site re-checking `.ok`).
   */
  async apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
    return this.rootFetch<T>(API_PREFIX + path, init);
  }

  /**
   * rootFetch is apiFetch WITHOUT the /api/v1 prefix — for the small number of deliberately
   * root-mounted routes (a long-lived SSE stream cannot survive /api/v1's 60s timeout —
   * /realtime/subscribe, /realtime/alerts/policy). Most callers want apiFetch, not this.
   */
  async rootFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
    const method = (init.method || "GET").toUpperCase();
    const headers = new Headers(init.headers);
    if (MUTATING.has(method) && this.csrfToken) {
      headers.set("X-CSRF-Token", this.csrfToken);
    }
    if (init.body && !headers.has("Content-Type")) {
      headers.set("Content-Type", "application/json");
    }
    const res = await fetch(path, { ...init, method, headers, credentials: "same-origin" });
    return readEnvelope<T>(res);
  }

  /**
   * login mints a session cookie (set by the server) and captures the CSRF token it returns.
   * Body keys are lowercase to match v1's own convention (mailbox.html's signIn) — the Go
   * handler's untagged struct fields match JSON keys case-insensitively either way, but
   * matching the established convention is one less thing to explain.
   */
  async login(email: string, password: string): Promise<LoginResult> {
    const result = await this.apiFetch<LoginResult>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });
    if (result.csrf_token) {
      this.csrfToken = result.csrf_token;
    }
    return result;
  }

  /**
   * stopImpersonating returns an impersonating session to the operator's own account.
   * This is the ONLY way back: an impersonated session is a different principal with a
   * different role, so any UI that offers impersonation MUST also offer this or it has
   * created a dead end (measured the hard way — the admin console refused the impersonated
   * session and rendered no way out of itself).
   */
  async stopImpersonating(): Promise<void> {
    await this.apiFetch("/auth/stop-impersonating", { method: "POST" });
  }

  async logout(): Promise<void> {
    try {
      await this.apiFetch("/auth/logout", { method: "POST" });
    } finally {
      this.csrfToken = "";
    }
  }

  /**
   * whoami reports the current session (or throws ApiError(401,...) if there isn't one) and
   * refreshes the held CSRF token — the same call mailbox.html's own boot sequence makes
   * before showing anything, and the way this client stays usable across a page reload
   * (the cookie survives; the in-memory CSRF token does not, until this is called again).
   */
  async whoami(): Promise<WhoAmI> {
    const who = await this.apiFetch<WhoAmI>("/auth/session");
    this.csrfToken = who.csrf_token;
    return who;
  }
}

/** isUnauthorized narrows an unknown catch value to "this needs a fresh login". */
export function isUnauthorized(err: unknown): boolean {
  return err instanceof ApiError && err.status === 401;
}
